import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cancelSaenggibuGeneration,
  generateSimilarSentencesStream,
  updateSaenggibuVersion,
} from '../../api/saenggibuApi';
import { calculateNEISBytes, getSaenggibuMaxBytes } from '../../utils/neisBytes';
import { hasExplicitSimilarLevelDistribution } from '../../utils/similarSentenceLaunch';
import TossLoadingSpinner from '../layout/TossLoadingSpinner';
import { useToast } from '../Toast';
import SaenggibuContentEditor from './SaenggibuContentEditor';
import SaenggibuResultActions, { copySaenggibuText } from './SaenggibuResultActions';
import { trackSaenggibuUxEvent } from '../../utils/eventTracker';
import './SimilarSentencePanel.css';

const FREE_MAX = 10;
const PRO_MAX = 150;
const MAX_INPUT_BYTES = 3000;
const COUNT_PRESETS = [5, 10, 30, 50, 100, 150];
const INLINE_COUNT_PRESETS = [5, 10, 20, 30];
const MAX_EVALUATION_CONTEXT_CHARS = 1500;
const STREAM_INACTIVITY_MS = 90_000;
const VISIBLE_RESULT_STEP = 30;

const EXAMPLE_SENTENCE = '모둠 활동에서 자료 정리를 맡아 역할 분담을 돕고, 발표 준비 과정에서 근거를 찾아 설명을 보강함.';
const CATEGORY_LABELS = {
  행동발달: '행동발달',
  교과: '교과/세특',
  교과_고등: '교과세특(고등)',
  창체: '창체',
  동아리: '동아리',
  진로: '진로',
};

const createJobId = () => {
  if (window.crypto?.randomUUID) {
    return `similar_${window.crypto.randomUUID()}`;
  }
  return `similar_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const resolveInitialCount = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= PRO_MAX ? parsed : 10;
};

const resolveInitialMode = (value) => (
  value === 'reinterpret' ? 'reinterpret' : 'structure_keep'
);

const resolveInitialLevels = (value) => {
  if (!value || typeof value !== 'object') return { 상: 3, 중: 4, 하: 3 };
  return {
    상: Math.max(0, Number(value.상) || 0),
    중: Math.max(0, Number(value.중) || 0),
    하: Math.max(0, Number(value.하) || 0),
  };
};

const SimilarSentencePanel = ({
  isProUser,
  isAnonymousUser,
  prefillSentence = '',
  initialResults = [],
  initialRecordId = '',
  onNeedLogin,
  onNeedPro,
  onOriginalChange,
  initialCategoryHint = '',
  initialTargetBytes = '',
  initialCount = 10,
  initialVariationMode = 'structure_keep',
  initialInstruction = '',
  initialLevelDistribution = null,
  selectedModel,
  variant = 'full',
  evaluationContext = '',
  sourceRecordId = '',
  onClose,
}) => {
  const isInline = variant === 'inline';
  const { showToast } = useToast();
  const navigate = useNavigate();
  const restoredCount = resolveInitialCount(initialCount);
  const restoredLevels = resolveInitialLevels(initialLevelDistribution);
  const [original, setOriginal] = useState(prefillSentence || '');
  const [count, setCount] = useState(restoredCount);
  const [variationMode, setVariationMode] = useState(
    resolveInitialMode(initialVariationMode)
  );
  const [targetBytes, setTargetBytes] = useState('');
  const [categoryHint, setCategoryHint] = useState('');
  const [instruction, setInstruction] = useState(initialInstruction || '');
  const [useLevels, setUseLevels] = useState(
    hasExplicitSimilarLevelDistribution(initialLevelDistribution)
  );
  const [levels, setLevels] = useState(restoredLevels);
  const [sentences, setSentences] = useState(initialResults);
  const [resultHasExplicitLevels, setResultHasExplicitLevels] = useState(
    hasExplicitSimilarLevelDistribution(initialLevelDistribution)
  );
  const [visibleResultCount, setVisibleResultCount] = useState(VISIBLE_RESULT_STEP);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [savedRecordId, setSavedRecordId] = useState(initialRecordId || '');
  const [error, setError] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [editOriginal, setEditOriginal] = useState('');
  const [savingEditIndex, setSavingEditIndex] = useState(null);
  const jobIdRef = useRef('');
  const cancelRequestedRef = useRef(false);

  useEffect(() => {
    if (prefillSentence) {
      setOriginal(prefillSentence);
      const nextBytes = Number(initialTargetBytes) || calculateNEISBytes(prefillSentence);
      if (nextBytes >= 100) setTargetBytes(String(nextBytes));
    }
    if (initialCategoryHint) {
      setCategoryHint(initialCategoryHint);
    }
    if (initialTargetBytes) {
      setTargetBytes(String(initialTargetBytes));
    }
  }, [initialCategoryHint, initialTargetBytes, prefillSentence]);

  useEffect(() => {
    if (initialResults.length > 0) {
      setSentences(initialResults);
      setResultHasExplicitLevels(
        hasExplicitSimilarLevelDistribution(initialLevelDistribution)
      );
      setVisibleResultCount(VISIBLE_RESULT_STEP);
      setEditingIndex(null);
      setEditDraft('');
      setEditOriginal('');
    }
    if (initialRecordId) {
      setSavedRecordId(initialRecordId);
    }
  }, [initialLevelDistribution, initialRecordId, initialResults]);

  const originalBytes = useMemo(() => calculateNEISBytes(original), [original]);
  const resultActionItems = useMemo(() => (
    resultHasExplicitLevels
      ? sentences
      : sentences.map((item) => ({ ...item, level: null }))
  ), [resultHasExplicitLevels, sentences]);
  const maxCount = isProUser ? PRO_MAX : FREE_MAX;
  const levelSum = (levels.상 || 0) + (levels.중 || 0) + (levels.하 || 0);
  const visibleSentences = sentences.slice(0, visibleResultCount);
  const progressTotal = Math.max(1, Number(progress.total) || count);
  const progressCurrent = Math.min(
    progressTotal,
    Math.max(0, Number(progress.current) || 0)
  );
  const progressPercent = Math.round((progressCurrent / progressTotal) * 100);

  const requestPaidCount = useCallback(() => {
    onNeedPro?.();
  }, [onNeedPro]);

  const applyCount = (nextCount) => {
    if (nextCount > FREE_MAX && !isProUser) {
      requestPaidCount();
      return;
    }
    const safe = Math.max(1, Math.min(maxCount, nextCount));
    setCount(safe);
    if (useLevels) {
      const mid = Math.floor(safe / 3);
      const high = Math.floor(safe / 3);
      setLevels({ 상: high, 중: mid, 하: safe - high - mid });
    }
  };

  const handleCopy = async (text, label, versionIndex = 0) => {
    const copied = await copySaenggibuText(text);
    if (copied) {
      trackSaenggibuUxEvent('saenggibu_copy', {
        saenggibu_category: '비슷한문장',
        is_batch: false,
        version_index: versionIndex,
      });
      showToast(`${label}을 복사했어요`, 'success');
    } else {
      showToast('복사하지 못했어요. 다시 시도해주세요', 'error');
    }
  };

  const closeEditor = () => {
    if (savingEditIndex !== null) return;
    setEditingIndex(null);
    setEditDraft('');
    setEditOriginal('');
  };

  const handleOpenEditor = (index) => {
    if (isGenerating || savingEditIndex !== null) return;
    if (editingIndex === index) {
      closeEditor();
      return;
    }
    if (editingIndex !== null) {
      showToast('수정 중인 문장을 먼저 저장하거나 취소해주세요', 'warning');
      return;
    }
    const content = sentences[index]?.content || '';
    setEditingIndex(index);
    setEditDraft(content);
    setEditOriginal(content);
  };

  const handleSaveEdit = async () => {
    if (editingIndex === null || savingEditIndex !== null) return;
    const finalContent = editDraft.trim();
    if (!finalContent) return;
    if (finalContent === editOriginal.trim()) {
      closeEditor();
      showToast('바뀐 내용이 없어요', 'info');
      return;
    }

    const itemIndex = editingIndex;
    const versionNumber = Number(sentences[itemIndex]?.version) || itemIndex + 1;
    const revision = {
      mode: 'manual_edit',
      instruction: '교사가 비슷한 문장을 직접 수정함',
      before_bytes: calculateNEISBytes(editOriginal),
      after_bytes: calculateNEISBytes(finalContent),
    };

    setSavingEditIndex(itemIndex);
    setSentences((previous) => previous.map((item, index) => (
      index === itemIndex
        ? {
            ...item,
            content: finalContent,
            byte_count: calculateNEISBytes(finalContent),
          }
        : item
    )));

    if (!savedRecordId) {
      setSavingEditIndex(null);
      setEditingIndex(null);
      setEditDraft('');
      setEditOriginal('');
      showToast('화면에는 반영했어요. 이 결과는 기록에 저장되지 않았어요', 'warning', 6000);
      return;
    }

    try {
      await updateSaenggibuVersion(
        savedRecordId,
        versionNumber,
        finalContent,
        revision
      );
      trackSaenggibuUxEvent('saenggibu_manual_edit', {
        saenggibu_category: '비슷한문장',
        is_batch: false,
        version_index: itemIndex,
      });
      window.dispatchEvent(new CustomEvent('saenggibuCreated', {
        detail: { recordId: savedRecordId },
      }));
      setEditingIndex(null);
      setEditDraft('');
      setEditOriginal('');
      showToast('수정한 내용을 기록에 저장했어요', 'success');
    } catch (editError) {
      console.error('비슷한 문장 직접 수정 저장 실패:', editError);
      showToast('화면에는 반영했어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    } finally {
      setSavingEditIndex(null);
    }
  };

  const handleCancel = async () => {
    if (!isGenerating || isCancelling) return;
    cancelRequestedRef.current = true;
    setIsCancelling(true);
    setProgress((prev) => ({ ...prev, message: '생성을 중단하고 있어요.' }));
    if (!jobIdRef.current) {
      cancelRequestedRef.current = false;
      setIsCancelling(false);
      setProgress((prev) => ({
        ...prev,
        message: '중단 준비가 끝나면 다시 눌러주세요.',
      }));
      return;
    }
    try {
      await cancelSaenggibuGeneration(jobIdRef.current);
    } catch {
      cancelRequestedRef.current = false;
      setIsCancelling(false);
      setProgress((prev) => ({
        ...prev,
        message: '중단 요청을 보내지 못했어요. 다시 눌러주세요.',
      }));
      showToast('생성 중단 요청을 보내지 못했어요. 다시 눌러주세요.', 'warning');
    }
  };

  const handleGenerate = useCallback(async () => {
    const trimmed = original.trim();
    if (!trimmed) {
      setError('바꿀 생기부 문장을 넣어주세요.');
      return;
    }
    if (originalBytes > MAX_INPUT_BYTES) {
      setError(`원문은 ${MAX_INPUT_BYTES}바이트까지 넣을 수 있어요.`);
      return;
    }
    if (count > FREE_MAX && !isProUser) {
      requestPaidCount();
      return;
    }
    if (useLevels && levelSum !== count) {
      setError('상·중·하 개수의 합이 만들 문장 수와 같아야 해요.');
      return;
    }

    cancelRequestedRef.current = false;
    const jobId = createJobId();
    jobIdRef.current = jobId;
    setError('');
    setIsGenerating(true);
    setIsCancelling(false);
    setEditingIndex(null);
    setEditDraft('');
    setEditOriginal('');
    setProgress({ current: 0, total: count, message: '비슷한 문장을 준비하고 있어요.' });

    const resolvedTarget = Number(targetBytes);
    const payload = {
      original_sentence: trimmed,
      count,
      variation_mode: variationMode,
      category_hint: categoryHint || null,
      instruction: instruction.trim() || null,
      generation_job_id: jobId,
      selected_model: selectedModel,
    };
    if (Number.isFinite(resolvedTarget) && resolvedTarget >= 100) {
      payload.target_bytes = Math.min(3000, resolvedTarget);
    }
    if (useLevels) {
      payload.level_distribution = levels;
    }
    if (isInline) {
      payload.launched_from = 'result_card';
      const context = String(evaluationContext || '').trim().slice(0, MAX_EVALUATION_CONTEXT_CHARS);
      if (context) payload.evaluation_context = context;
      if (sourceRecordId) payload.source_record_id = sourceRecordId;
    }

    let cancelled = false;
    let receivedCount = 0;
    let receivedNewResult = false;
    try {
      await generateSimilarSentencesStream(payload, {
        inactivityTimeoutMs: STREAM_INACTIVITY_MS,
        onProgress: (event) => {
          setProgress({
            current: event.current || 0,
            total: event.total || count,
            message: event.message || '문장을 만들고 있어요.',
          });
        },
        onChunk: (event) => {
          const incoming = Array.isArray(event.sentences) ? event.sentences : [];
          const offset = Number(event.offset) || 0;
          if (incoming.length > 0 && !receivedNewResult) {
            receivedNewResult = true;
            setSavedRecordId('');
            setResultHasExplicitLevels(useLevels);
          }
          receivedCount = event.replayed
            ? incoming.length
            : offset + incoming.length;
          setSentences((prev) => {
            if (event.replayed || offset === 0) return incoming;
            const next = prev.slice(0, offset);
            return [...next, ...incoming];
          });
          setVisibleResultCount(VISIBLE_RESULT_STEP);
          setProgress({
            current: receivedCount,
            total: event.total || count,
            message: `${receivedCount}개 문장을 만들었어요.`,
          });
        },
        onSaved: (event) => {
          if (event.record_id) {
            setSavedRecordId(event.record_id);
          }
        },
        onCancelled: () => {
          cancelled = true;
          setError('');
          showToast('생성을 중단했어요', 'info');
        },
        onError: (event) => {
          setError(event.message || '비슷한 문장을 만들지 못했어요.');
        },
      });
      if (!cancelRequestedRef.current && !cancelled) {
        if (receivedCount > 0) {
          showToast('비슷한 문장을 만들었어요', 'success');
        } else {
          setError('비슷한 문장을 만들지 못했어요.');
        }
      }
    } catch (err) {
      if (cancelRequestedRef.current || cancelled) {
        setError('');
        if (!cancelled) {
          showToast('생성을 중단했어요', 'info');
        }
        return;
      }
      if (String(err?.message || '').includes('401') || String(err?.message || '').includes('인증')) {
        onNeedLogin?.();
        return;
      }
      if (String(err?.message || '').includes('프로') || String(err?.message || '').includes('무료')) {
        requestPaidCount();
      }
      setError(err?.message || '비슷한 문장을 만들지 못했어요.');
    } finally {
      setIsGenerating(false);
      setIsCancelling(false);
      jobIdRef.current = '';
    }
  }, [
    categoryHint,
    count,
    evaluationContext,
    instruction,
    isInline,
    isProUser,
    levelSum,
    levels,
    onNeedLogin,
    original,
    originalBytes,
    requestPaidCount,
    selectedModel,
    showToast,
    sourceRecordId,
    targetBytes,
    useLevels,
    variationMode,
  ]);

  const countPresets = isInline ? INLINE_COUNT_PRESETS : COUNT_PRESETS;

  return (
    <div className={`similar-sentence-panel${isInline ? ' is-inline' : ''}`}>
      {isInline ? (
        <div className="similar-sentence-panel__inline-head">
          <p>
            <strong>이 문장으로 비슷한 문장 만들기</strong>
            <span>사실은 그대로, 표현만 학생마다 다르게</span>
          </p>
          {!isGenerating && (
            <button type="button" onClick={onClose} aria-label="비슷한 문장 닫기">닫기</button>
          )}
        </div>
      ) : (
        <>
          {(categoryHint || Number(targetBytes) >= 100) && (
            <p className="similar-sentence-panel__context">
              {categoryHint ? (CATEGORY_LABELS[categoryHint] || categoryHint) : '유형 미지정'}
              {Number(targetBytes) >= 100 ? ` · 목표 ${targetBytes}B` : ''}
            </p>
          )}

          <label className="similar-sentence-panel__label" htmlFor="similar-original">
            바꿀 문장
          </label>
          <textarea
            id="similar-original"
            className="similar-sentence-panel__textarea"
            value={original}
            onChange={(event) => {
              const next = event.target.value;
              setOriginal(next);
              onOriginalChange?.(next);
            }}
            placeholder={EXAMPLE_SENTENCE}
            disabled={isGenerating}
          />
          <div className={`similar-sentence-panel__bytes${originalBytes > MAX_INPUT_BYTES ? ' is-over' : ''}`}>
            {originalBytes} / {MAX_INPUT_BYTES}B
          </div>
        </>
      )}

      <p id="similar-count-label" className="similar-sentence-panel__label">몇 개 만들까요?</p>
      <div
        className="similar-sentence-panel__chips"
        role="group"
        aria-labelledby="similar-count-label"
      >
        {countPresets.map((preset) => {
          const locked = preset > FREE_MAX && !isProUser;
          return (
            <button
              key={preset}
              type="button"
              className={`similar-sentence-panel__chip${count === preset ? ' is-active' : ''}${locked ? ' is-locked' : ''}`}
              onClick={() => applyCount(preset)}
              disabled={isGenerating}
              aria-pressed={count === preset}
            >
              {preset}개{locked ? ' 프로' : ''}
            </button>
          );
        })}
      </div>
      {!isProUser && isInline && (
        <p className="similar-sentence-panel__plan-hint">10개까지 무료 · 20개부터 프로</p>
      )}
      {!isProUser && !isInline && (
        <p className="similar-sentence-panel__plan-hint">
          {isAnonymousUser
            ? '10개 무료 체험 · 로그인하면 기록 저장 · 30개부터 프로'
            : '10개 무료 · 기록 자동 저장 · 30개부터 프로'}
        </p>
      )}

      <p id="similar-mode-label" className="similar-sentence-panel__label">어떻게 바꿀까요?</p>
      <div
        className="similar-sentence-panel__modes"
        role="group"
        aria-labelledby="similar-mode-label"
      >
        <button
          type="button"
          className={`similar-sentence-panel__mode${variationMode === 'structure_keep' ? ' is-active' : ''}`}
          onClick={() => setVariationMode('structure_keep')}
          disabled={isGenerating}
          aria-pressed={variationMode === 'structure_keep'}
        >
          <strong>비슷하게</strong>
          <span>구조는 두고 표현만 바꿉니다</span>
        </button>
        <button
          type="button"
          className={`similar-sentence-panel__mode${variationMode === 'reinterpret' ? ' is-active' : ''}`}
          onClick={() => setVariationMode('reinterpret')}
          disabled={isGenerating}
          aria-pressed={variationMode === 'reinterpret'}
        >
          <strong>다양하게</strong>
          <span>같은 사실이지만 문장 구조를 바꿉니다</span>
        </button>
      </div>

      {!isInline && (
      <div id="similar-advanced-settings" className="similar-sentence-panel__advanced">
          <label className="similar-sentence-panel__label">
            목표 바이트
            <input
              type="number"
              min={100}
              max={3000}
              value={targetBytes}
              onChange={(event) => setTargetBytes(event.target.value)}
              placeholder={originalBytes ? String(originalBytes) : '원문과 비슷하게'}
              disabled={isGenerating}
            />
          </label>
          <label className="similar-sentence-panel__label">
            문장 유형
            <select
              value={categoryHint}
              onChange={(event) => setCategoryHint(event.target.value)}
              disabled={isGenerating}
            >
              <option value="">선택 안 함</option>
              <option value="행동발달">행동발달</option>
              <option value="교과">교과/세특</option>
              <option value="교과_고등">교과세특(고등)</option>
              <option value="창체">창체</option>
              <option value="동아리">동아리</option>
              <option value="진로">진로</option>
            </select>
          </label>
          <label className="similar-sentence-panel__check is-wide">
            <input
              type="checkbox"
              checked={useLevels}
              onChange={(event) => {
                const next = event.target.checked;
                setUseLevels(next);
                if (next) applyCount(count);
              }}
              disabled={isGenerating}
            />
            상·중·하로 나눠 만들기
          </label>
          {useLevels && (
            <div className="similar-sentence-panel__levels is-wide">
              {['상', '중', '하'].map((level) => (
                <label key={level}>
                  {level}
                  <input
                    type="number"
                    min={0}
                    max={count}
                    value={levels[level]}
                    onChange={(event) => setLevels((prev) => ({
                      ...prev,
                      [level]: Math.max(0, Number(event.target.value) || 0),
                    }))}
                    disabled={isGenerating}
                  />
                </label>
              ))}
              <span className={levelSum === count ? '' : 'is-over'}>{levelSum}/{count}개</span>
            </div>
          )}
          <label className="similar-sentence-panel__label is-wide">
            추가 요청
            <input
              type="text"
              maxLength={500}
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="예: 활동명은 그대로 두고 표현만 바꿔줘"
              disabled={isGenerating}
            />
          </label>
      </div>
      )}

      {error && <p className="similar-sentence-panel__error" role="alert">{error}</p>}

      {!isGenerating && (
        <button
          type="button"
          className="similar-sentence-panel__cta"
          onClick={handleGenerate}
        >
          {count}개 만들기
        </button>
      )}

      {isGenerating && (
        <section
          className="similar-sentence-panel__loading-card"
          aria-labelledby="similar-sentence-loading-title"
        >
          <div className="similar-sentence-panel__loading-head">
            <h3 id="similar-sentence-loading-title">생성 결과</h3>
            <span>작성 중</span>
          </div>
          <div
            className="similar-sentence-panel__loading-status"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <TossLoadingSpinner
              type="saenggibu"
              message={progress.message || '비슷한 문장을 만들고 있어요.'}
              size="medium"
              showIcon
              showProgress
              progressValue={progressPercent}
              progressLabel={`${progressCurrent}/${progressTotal}개`}
              progressAriaLabel="비슷한 문장 생성 진행률"
              showEstimatedTime={false}
            />
          </div>
          {progressCurrent === 0 && sentences.length === 0 && (
            <div
              className="similar-sentence-panel__loading-skeletons"
              aria-hidden="true"
            >
              {[0, 1].map((index) => (
                <div key={index} className="similar-sentence-panel__loading-skeleton">
                  <span />
                  <span />
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            className="similar-sentence-panel__cancel"
            onClick={handleCancel}
            disabled={isCancelling}
          >
            {isCancelling ? '중단 중' : '생성 중단'}
          </button>
        </section>
      )}

      {sentences.length > 0 && (
        <div className="similar-sentence-panel__results">
          <div className="similar-sentence-panel__result-head">
            <p>{sentences.length}개 문장</p>
            <SaenggibuResultActions
              ariaLabel="비슷한 문장 결과 작업"
              filenamePrefix="비슷한문장"
              items={resultActionItems}
              sheetName="비슷한문장"
            />
          </div>
          {savedRecordId && (
            <p className="similar-sentence-panel__saved">
              기록에 저장했어요.
              <button type="button" onClick={() => navigate('/saenggibu-history')}>
                기록에서 보기
              </button>
            </p>
          )}
          {isAnonymousUser && (
            <p className="similar-sentence-panel__saved">로그인하면 만든 문장을 기록에 남겨 둘 수 있어요.</p>
          )}
          <p className="similar-sentence-panel__notice">
            만든 문장은 초안이에요. 학생별로 관찰한 사실을 반영해 고친 뒤 사용해 주세요.
          </p>
          <ol className="similar-sentence-panel__list">
            {visibleSentences.map((item, index) => (
              <li
                key={`${item.version || index}-${index}`}
                aria-label={`${index + 1}번 문장`}
              >
                <div className="similar-sentence-panel__card-top">
                  <div className="similar-sentence-panel__card-meta">
                    <span>{index + 1}</span>
                    {resultHasExplicitLevels && item.level && (
                      <em aria-label={`${item.level} 수준`}>{item.level}</em>
                    )}
                    <small>{item.byte_count || calculateNEISBytes(item.content)}B</small>
                  </div>
                  <div className="similar-sentence-panel__card-actions">
                    {!isGenerating && (
                      <button
                        type="button"
                        className="similar-sentence-panel__edit"
                        aria-pressed={editingIndex === index}
                        disabled={savingEditIndex !== null}
                        onClick={() => handleOpenEditor(index)}
                      >
                        {editingIndex === index ? '수정 닫기' : '직접 수정'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="similar-sentence-panel__copy"
                      onClick={() => handleCopy(item.content, `${index + 1}번 문장`, index)}
                    >
                      복사
                    </button>
                  </div>
                </div>
                <p>{item.content}</p>
                {editingIndex === index && (
                  <SaenggibuContentEditor
                    ariaLabel={`${index + 1}번 문장 수정`}
                    isSaving={savingEditIndex === index}
                    maxBytes={getSaenggibuMaxBytes('비슷한문장')}
                    onCancel={closeEditor}
                    onChange={setEditDraft}
                    onSave={handleSaveEdit}
                    rows={7}
                    targetBytes={Number(targetBytes) >= 100 ? targetBytes : null}
                    value={editDraft}
                    variant="inline"
                  />
                )}
              </li>
            ))}
          </ol>
          {visibleResultCount < sentences.length && (
            <button
              type="button"
              className="similar-sentence-panel__show-more"
              onClick={() => setVisibleResultCount((prev) => (
                Math.min(sentences.length, prev + VISIBLE_RESULT_STEP)
              ))}
            >
              {Math.min(VISIBLE_RESULT_STEP, sentences.length - visibleResultCount)}개 더 보기
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SimilarSentencePanel;
